/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * MacroDashboard.tsx
 * Full macro intelligence panel for the Terminal.
 * Shows yield curve visualization, rate trend sparklines,
 * CRE-specific FRED indicators, and upcoming macro events calendar.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, Area, AreaChart, Bar, BarChart,
} from 'recharts';

const API = 'https://coast-terminal-production.up.railway.app';

interface MacroDashboardProps {
  token: string;
  rates: any;
}

// Upcoming macro events — hardcoded calendar, updated quarterly
// In production this would come from an API like Econoday
const MACRO_CALENDAR = [
  { date: '2026-04-10', event: 'CPI Report (March)', impact: 'high', category: 'inflation' },
  { date: '2026-04-11', event: 'PPI Report (March)', impact: 'medium', category: 'inflation' },
  { date: '2026-04-16', event: 'Retail Sales (March)', impact: 'medium', category: 'consumer' },
  { date: '2026-04-17', event: 'Housing Starts (March)', impact: 'high', category: 'housing' },
  { date: '2026-04-17', event: 'Building Permits (March)', impact: 'high', category: 'housing' },
  { date: '2026-04-23', event: 'Existing Home Sales (March)', impact: 'medium', category: 'housing' },
  { date: '2026-04-30', event: 'GDP Growth Rate Q1 2026 (Advance)', impact: 'high', category: 'growth' },
  { date: '2026-05-02', event: 'Jobs Report (April)', impact: 'high', category: 'employment' },
  { date: '2026-05-06', event: 'FOMC Meeting Begins', impact: 'high', category: 'fed' },
  { date: '2026-05-07', event: 'Fed Rate Decision', impact: 'high', category: 'fed' },
  { date: '2026-05-12', event: 'CPI Report (April)', impact: 'high', category: 'inflation' },
  { date: '2026-05-15', event: 'Housing Starts (April)', impact: 'high', category: 'housing' },
];

const CATEGORY_COLORS: Record<string, string> = {
  fed:        '#b8956a',
  inflation:  '#CC2200',
  housing:    '#1A5A8A',
  employment: '#1A7A4A',
  growth:     '#8A5A9A',
  consumer:   '#4A7A9A',
};

const MacroDashboard: React.FC<MacroDashboardProps> = ({ token, rates }) => {
  const gold = '#b8956a';
  const green = '#1A7A4A';
  const red = '#CC2200';
  const surface = '#fafaf8';
  const surfaceRaised = '#f2ede6';
  const surfaceHigh = '#e6ddd4';
  const border = '#e6ddd4';
  const textPrimary = '#0d3d5c';
  const textSecondary = '#12526e';
  const textMuted = '#2d7fa0';
  const serifFont = "'Fraunces', Georgia, serif";

  const headers = { Authorization: `Bearer ${token}` };

  const [yieldCurve, setYieldCurve] = useState<any>(null);
  const [creIndicators, setCreIndicators] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [ycRes, creRes] = await Promise.all([
        axios.get(`${API}/terminal/yield-curve`, { headers }),
        axios.get(`${API}/terminal/cre-indicators`, { headers }),
      ]);
      setYieldCurve(ycRes.data);
      setCreIndicators(creRes.data);
    } catch {}
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { fetchData(); }, [fetchData]);

  // Build yield curve snapshot data for bar chart
  const yieldCurveSnapshot = yieldCurve ? [
    { label: '2Y',  value: yieldCurve['2y']?.[0]?.value },
    { label: '5Y',  value: yieldCurve['5y']?.[0]?.value },
    { label: '10Y', value: yieldCurve['10y']?.[0]?.value },
    { label: '30Y', value: yieldCurve['30y']?.[0]?.value },
  ].filter(d => d.value != null) : [];

  // Build 10Y-2Y spread history for area chart
  const spreadHistory = yieldCurve?.spread_10y_2y
    ?.slice(0, 30)
    .reverse()
    .map((d: any) => ({
      date: d.date?.slice(5),  // MM-DD
      value: d.value,
    })) || [];

  // Build rate history charts from yield curve data
  const treasury10yHistory = yieldCurve?.['10y']
    ?.slice(0, 30)
    .reverse()
    .map((d: any) => ({ date: d.date?.slice(5), value: d.value })) || [];

  const treasury2yHistory = yieldCurve?.['2y']
    ?.slice(0, 30)
    .reverse()
    .map((d: any) => ({ date: d.date?.slice(5), value: d.value })) || [];

  // Upcoming events (next 30 days)
  const today = new Date();
  const in30 = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  const upcomingEvents = MACRO_CALENDAR.filter(e => {
    const d = new Date(e.date);
    return d >= today && d <= in30;
  }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const daysUntil = (dateStr: string) => {
    const diff = Math.floor((new Date(dateStr).getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    return `${diff}d`;
  };

  const tooltipStyle = {
    backgroundColor: '#fafaf8',
    border: `1px solid ${border}`,
    borderRadius: '2px',
    color: textPrimary,
    fontSize: '11px',
  };

  if (loading) return (
    <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', backgroundColor: border }}>
      {[1,2,3,4].map(i => (
        <div key={i} style={{ backgroundColor: '#ffffff', padding: '20px', height: '180px' }}>
          <div style={{ height: '10px', width: '40%', backgroundColor: '#fafaf8', marginBottom: '16px', animation: 'pulse 1.5s ease-in-out infinite' }} />
          <div style={{ height: '100px', backgroundColor: '#fafaf8', animation: 'pulse 1.5s ease-in-out infinite' }} />
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ padding: '0' }}>

      {/* ── Section header ── */}
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${border}`, backgroundColor: '#ffffff' }}>
        <div style={{ fontSize: '9px', color: gold, letterSpacing: '3px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Macro Intelligence</div>
        <div style={{ fontSize: '18px', fontWeight: 400, color: textPrimary, fontFamily: serifFont }}>Interest Rate Environment & CRE Indicators</div>
      </div>

      {/* ── Main chart grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', backgroundColor: border }}>

        {/* Yield Curve Snapshot */}
        <div style={{ backgroundColor: '#ffffff', padding: '18px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <div>
              <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase' }}>Yield Curve</div>
              <div style={{ fontSize: '13px', color: textPrimary, fontWeight: 600, marginTop: '2px', fontFamily: serifFont }}>Treasury Term Structure</div>
            </div>
            {yieldCurve?.inverted && (
              <div style={{ backgroundColor: 'rgba(204,34,0,0.1)', border: `1px solid rgba(204,34,0,0.3)`, padding: '3px 8px', fontSize: '9px', fontWeight: 800, color: red, letterSpacing: '1px' }}>
                ⚠ INVERTED
              </div>
            )}
            {yieldCurve?.current_spread !== null && yieldCurve?.current_spread !== undefined && !yieldCurve?.inverted && (
              <div style={{ fontSize: '11px', color: green, fontWeight: 700 }}>
                +{yieldCurve.current_spread?.toFixed(2)}% spread
              </div>
            )}
          </div>
          <ResponsiveContainer width="100%" height={130}>
            <BarChart data={yieldCurveSnapshot} barSize={28}>
              <XAxis dataKey="label" tick={{ fill: textMuted, fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: textMuted, fontSize: 9 }} axisLine={false} tickLine={false} domain={['auto', 'auto']} tickFormatter={v => `${v}%`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => [`${Number(v).toFixed(2)}%`, 'Yield']} />
              <ReferenceLine y={0} stroke={border} />
              <Bar dataKey="value" fill={gold} radius={[1, 1, 0, 0]}
                label={{ position: 'top', fontSize: 9, fill: textSecondary, formatter: (v: any) => `${Number(v).toFixed(2)}%` }} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 10Y-2Y Spread History */}
        <div style={{ backgroundColor: '#ffffff', padding: '18px 20px' }}>
          <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase' }}>30-Day History</div>
            <div style={{ fontSize: '13px', color: textPrimary, fontWeight: 600, marginTop: '2px', fontFamily: serifFont }}>10Y − 2Y Spread</div>
          </div>
          <ResponsiveContainer width="100%" height={130}>
            <AreaChart data={spreadHistory}>
              <defs>
                <linearGradient id="spreadGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={yieldCurve?.inverted ? red : green} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={yieldCurve?.inverted ? red : green} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: textMuted, fontSize: 8 }} axisLine={false} tickLine={false}
                interval={Math.floor(spreadHistory.length / 4)} />
              <YAxis tick={{ fill: textMuted, fontSize: 9 }} axisLine={false} tickLine={false}
                tickFormatter={v => `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%`} />
              <Tooltip contentStyle={tooltipStyle}
                formatter={(v: any) => [`${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(3)}%`, 'Spread']} />
              <ReferenceLine y={0} stroke={red} strokeDasharray="3 3" label={{ value: 'Inversion', fill: red, fontSize: 8 }} />
              <Area type="monotone" dataKey="value" stroke={yieldCurve?.inverted ? red : green}
                strokeWidth={2} fill="url(#spreadGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* 10Y Treasury History */}
        <div style={{ backgroundColor: '#ffffff', padding: '18px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <div>
              <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase' }}>30-Day Trend</div>
              <div style={{ fontSize: '13px', color: textPrimary, fontWeight: 600, marginTop: '2px', fontFamily: serifFont }}>10-Year Treasury</div>
            </div>
            {rates?.treasury_10y && (
              <div style={{ fontSize: '20px', fontWeight: 700, color: gold, fontFamily: serifFont }}>{rates.treasury_10y}%</div>
            )}
          </div>
          <ResponsiveContainer width="100%" height={100}>
            <LineChart data={treasury10yHistory}>
              <XAxis dataKey="date" tick={{ fill: textMuted, fontSize: 8 }} axisLine={false} tickLine={false}
                interval={Math.floor(treasury10yHistory.length / 4)} />
              <YAxis tick={{ fill: textMuted, fontSize: 9 }} axisLine={false} tickLine={false}
                domain={['auto', 'auto']} tickFormatter={v => `${Number(v).toFixed(1)}%`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => [`${Number(v).toFixed(2)}%`, '10Y TSY']} />
              <Line type="monotone" dataKey="value" stroke={gold} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* 2Y Treasury History */}
        <div style={{ backgroundColor: '#ffffff', padding: '18px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <div>
              <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase' }}>30-Day Trend</div>
              <div style={{ fontSize: '13px', color: textPrimary, fontWeight: 600, marginTop: '2px', fontFamily: serifFont }}>2-Year Treasury</div>
            </div>
            {rates?.treasury_2y && (
              <div style={{ fontSize: '20px', fontWeight: 700, color: textSecondary, fontFamily: serifFont }}>{rates.treasury_2y}%</div>
            )}
          </div>
          <ResponsiveContainer width="100%" height={100}>
            <LineChart data={treasury2yHistory}>
              <XAxis dataKey="date" tick={{ fill: textMuted, fontSize: 8 }} axisLine={false} tickLine={false}
                interval={Math.floor(treasury2yHistory.length / 4)} />
              <YAxis tick={{ fill: textMuted, fontSize: 9 }} axisLine={false} tickLine={false}
                domain={['auto', 'auto']} tickFormatter={v => `${Number(v).toFixed(1)}%`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => [`${Number(v).toFixed(2)}%`, '2Y TSY']} />
              <Line type="monotone" dataKey="value" stroke={textSecondary} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── CRE Indicators ── */}
      {creIndicators && Object.keys(creIndicators).length > 0 && (
        <div style={{ borderTop: `1px solid ${border}` }}>
          <div style={{ padding: '12px 20px', borderBottom: `1px solid ${border}`, backgroundColor: '#fafaf8' }}>
            <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase' }}>CRE-Specific Indicators · Federal Reserve (FRED)</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1px', backgroundColor: border }}>
            {Object.entries(creIndicators).map(([key, ind]: [string, any]) => {
              const isPositive = ind.change > 0;
              // For vacancy and delinquency, higher = worse
              const isBad = key === 'vacancy_rate' || key === 'cre_delinq';
              const color = isBad
                ? (isPositive ? red : green)
                : (isPositive ? green : red);
              return (
                <div key={key} style={{ backgroundColor: '#ffffff', padding: '18px 20px' }}>
                  <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '8px' }}>{ind.label}</div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', marginBottom: '6px' }}>
                    <div style={{ fontSize: '28px', fontWeight: 700, color: gold, lineHeight: 1, fontFamily: serifFont }}>
                      {ind.value?.toFixed(1)}
                    </div>
                    <div style={{ fontSize: '11px', color: textMuted, marginBottom: '4px' }}>{ind.unit}</div>
                  </div>
                  <div style={{ fontSize: '10px', color }}>
                    {isPositive ? '▲' : '▼'} {Math.abs(ind.change).toFixed(2)} vs prior period
                  </div>
                  <div style={{ fontSize: '9px', color: textMuted, marginTop: '2px' }}>As of {ind.date}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Macro Calendar ── */}
      <div style={{ borderTop: `1px solid ${border}` }}>
        <div style={{ padding: '12px 20px', borderBottom: `1px solid ${border}`, backgroundColor: '#fafaf8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase' }}>Macro Calendar · Next 30 Days</div>
          <div style={{ fontSize: '9px', color: textMuted }}>High-impact events affecting CRE markets</div>
        </div>
        {upcomingEvents.length === 0 ? (
          <div style={{ padding: '24px 20px', textAlign: 'center', fontSize: '12px', color: textMuted }}>No major events in the next 30 days.</div>
        ) : (
          <div>
            {upcomingEvents.map((event, i) => {
              const catColor = CATEGORY_COLORS[event.category] || textMuted;
              const days = daysUntil(event.date);
              const isImminent = days === 'Today' || days === 'Tomorrow' || parseInt(days) <= 3;
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', padding: '10px 20px',
                  borderBottom: `1px solid ${border}`,
                  backgroundColor: isImminent ? 'rgba(201,169,110,0.04)' : 'transparent',
                  transition: 'background 0.1s',
                }}>
                  <div style={{ width: '52px', flexShrink: 0 }}>
                    <div style={{ fontSize: '10px', fontWeight: 800, color: isImminent ? gold : textSecondary }}>{days}</div>
                    <div style={{ fontSize: '9px', color: textMuted }}>{new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                  </div>
                  <div style={{ width: '2px', height: '28px', backgroundColor: catColor, margin: '0 14px', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: textPrimary, fontWeight: 600 }}>{event.event}</div>
                    <div style={{ fontSize: '9px', color: catColor, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700, marginTop: '2px' }}>{event.category}</div>
                  </div>
                  {event.impact === 'high' && (
                    <div style={{ fontSize: '8px', fontWeight: 800, color: red, letterSpacing: '1px', textTransform: 'uppercase', padding: '2px 6px', border: `1px solid rgba(204,34,0,0.3)` }}>HIGH IMPACT</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
};

export default MacroDashboard;